import { inject, Injectable } from '@angular/core';
import { EnvironmentConfig } from 'src/app/configs/environment.config';
import { fixedEnvironment } from 'src/environments/environment.fixed';
import { ApolloClient, ApolloLink, CombinedGraphQLErrors, InMemoryCache, ServerError } from '@apollo/client';
import { HttpLink } from 'apollo-angular/http';
import { v4 as uuidv4 } from 'uuid';
import { ErrorLink } from '@apollo/client/link/error';
import { TranslateService } from '@ngx-translate/core';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { map } from 'rxjs/operators';
import { StorageService } from '@src/app/services/storage/storage.service.interface';
import { RetailerScope } from '@src/app/model/retailer-scope';
import { StorageSelectors } from '@src/app/services/storage/storage.selectors';

export const loadApolloConfig = (): ApolloClient.Options => {
  const httpLink = inject(HttpLink);
  const apolloConfig = inject(ApolloServiceConfig);
  const translateService = inject(TranslateService);
  const storageService = inject(StorageService);
  const _userIdent = `${uuidv4()}`;

  const errorLink = new ErrorLink(({ error, operation }) => {
    if (CombinedGraphQLErrors.is(error)) {
      if (!operation.getContext().noAlert) {
        alert(translateService.instant('errors.graphQLError')
          + `:\n${error.errors.filter(e => e?.extensions?.code).map(e => translateService.instant(`errors.${e.extensions.code}`)).join(', ')}`
          + `:\n${error.errors.filter(e => e?.extensions?.error).map(e => e.extensions.error)}`
        );
      }
    }
    if (ServerError.is(error)) {
      if (error.statusCode === 400) {
        alert('ERROR 400: Bad request');
      }
      if (error.statusCode === 500) {
        alert('ERROR 500: Server error');
      }
    }
  });

  const apolloLink = httpLink.create({
    uri: (operation: ApolloLink.Operation) => apolloConfig.backendUrl + apolloConfig.gqlEntryPoint + '?' + new URLSearchParams({
      _o: operation?.operationName,
      _i: _userIdent,
      _s: storageService.readFromStorage<RetailerScope>(StorageSelectors.retailerScope)?.id?.toString(),
    }).toString(),
    withCredentials: apolloConfig.withCredentials,
  });

  return {
    link: ApolloLink.from([errorLink, apolloLink]),
    defaultOptions: apolloConfig.defaultOptions,
    cache: new InMemoryCache(),
  };
};

@Injectable({
  providedIn: 'root',
})
export class ApolloServiceConfig {
  public backendUrl: string = 'http://localhost:9000';
  public gqlEntryPoint: string = '/graphql/graphql';
  public withCredentials = true;
  public defaultOptions: ApolloClient.DefaultOptions = {
    watchQuery: {
      errorPolicy: 'all',
    },
  };
}

export function createApolloServiceConfig(env?: EnvironmentConfig): ApolloServiceConfig {
  const config = new ApolloServiceConfig();

  if (env) {
    if (env.backendUrl) {
      config.backendUrl = env.backendUrl;
    }

    if (fixedEnvironment.gqlEntryPoint) {
      config.gqlEntryPoint = fixedEnvironment.gqlEntryPoint;
    }
  }

  return config;
}
